import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { SpecraButton } from '../specra/ensigns';
import { PageContainer } from './PageContainer';
/**
 * Simple skeleton placeholder component
 */ function Skeleton({ className }) {
    return /*#__PURE__*/ _jsx("div", {
        className: `animate-pulse bg-muted rounded ${className ?? ''}`
    });
}
/**
 * LoadingState displays a skeleton loading UI.
 * The page title is handled by DashboardLayout's metadata.
 *
 * @example
 * if (isLoading) return <LoadingState rows={3} />;
 */ export function LoadingState({ rows = 3 }) {
    return /*#__PURE__*/ _jsx(PageContainer, {
        children: /*#__PURE__*/ _jsx("div", {
            className: "space-y-4",
            "data-testid": Testids.pageState.loading,
            children: Array.from({
                length: rows
            }, (_, i)=>`skeleton-row-${i}`).map((rowKey)=>/*#__PURE__*/ _jsx("div", {
                    className: "rounded-lg border p-4",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx(Skeleton, {
                                className: "h-5 w-1/3"
                            }),
                            /*#__PURE__*/ _jsx(Skeleton, {
                                className: "h-4 w-2/3"
                            }),
                            /*#__PURE__*/ _jsx(Skeleton, {
                                className: "h-4 w-1/2"
                            })
                        ]
                    })
                }, rowKey))
        })
    });
}
/**
 * ErrorState displays an error message with optional retry button.
 *
 * @example
 * if (error) return <ErrorState message={error.message} onRetry={refetch} />;
 */ export function ErrorState({ title = 'Something went wrong', message, onRetry }) {
    return /*#__PURE__*/ _jsx(PageContainer, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col items-center justify-center py-12 text-center",
            "data-testid": Testids.pageState.error,
            role: "alert",
            children: [
                /*#__PURE__*/ _jsx(AlertCircle, {
                    className: "h-12 w-12 text-destructive mb-4"
                }),
                /*#__PURE__*/ _jsx("h2", {
                    className: "text-xl font-semibold mb-2",
                    children: title
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-muted-foreground mb-4 max-w-md",
                    children: message
                }),
                onRetry && /*#__PURE__*/ _jsxs(SpecraButton, {
                    onClick: onRetry,
                    variant: "secondary",
                    children: [
                        /*#__PURE__*/ _jsx(RefreshCw, {
                            className: "mr-2 h-4 w-4"
                        }),
                        "Try again"
                    ]
                })
            ]
        })
    });
}
/**
 * EmptyState displays a message when no content is available.
 *
 * @example
 * if (!data.length) return (
 *   <EmptyState
 *     title="No projects yet"
 *     description="Create your first project to get started"
 *     action={<Button>Create Project</SpecraButton>}
 *   />
 * );
 */ export function EmptyState({ icon, title, description, action }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col items-center justify-center py-12 text-center",
        "data-testid": Testids.pageState.empty,
        children: [
            icon && /*#__PURE__*/ _jsx("div", {
                className: "mb-4 text-muted-foreground",
                children: icon
            }),
            /*#__PURE__*/ _jsx("h3", {
                className: "text-lg font-semibold mb-2",
                children: title
            }),
            description && /*#__PURE__*/ _jsx("p", {
                className: "text-muted-foreground mb-4 max-w-md",
                children: description
            }),
            action
        ]
    });
}
